import type { DatePickerValue } from '@civilgrid/ui/components';
import { Button, DatePicker, Dialog, DialogBody, DialogContent, DialogFooter, Label } from '@civilgrid/ui/components';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { outcomeLabels, requestStatusLabels, utilitiesPresentLabels } from '~/common/dataRequestUtils';
import { Outcome, RequestStatus, UtilityPresence } from '~/common/types';
import Select from '~/components2/forms/Select';
import type { FollowUpFields } from './useBulkEditFollowUp';

// Mirrors FollowUpPanel helpers
function getContactedAtFieldName(status: RequestStatus): keyof FollowUpFields | null {
  const map: Partial<Record<RequestStatus, keyof FollowUpFields>> = {
    [RequestStatus.InProgress0]: 'firstContactedAt',
    [RequestStatus.InProgress1]: 'secondContactedAt',
    [RequestStatus.InProgress2]: 'thirdContactedAt',
    [RequestStatus.InProgress3]: 'fourthContactedAt',
  };
  return map[status] ?? null;
}

const CONTACTED_AT_LABELS: Partial<Record<keyof FollowUpFields, string>> = {
  firstContactedAt: 'First Contacted At',
  secondContactedAt: 'Second Contacted At',
  thirdContactedAt: 'Third Contacted At',
  fourthContactedAt: 'Fourth Contacted At',
};

function isCompleteStatus(status: RequestStatus): boolean {
  return status === RequestStatus.Complete || status === RequestStatus.Cancelled || status === RequestStatus.Dismissed;
}

const requestStatusItems = Object.values(RequestStatus).map((value) => ({
  value,
  label: requestStatusLabels[value],
}));

const outcomeItems = Object.values(Outcome).map((value) => ({
  value,
  label: outcomeLabels[value],
}));

const utilityPresenceItems = Object.values(UtilityPresence).map((value) => ({
  value,
  label: utilitiesPresentLabels[value],
}));

function isoToDatePickerValue(iso: string | undefined): DatePickerValue {
  return iso ? { date: iso, view: 'Day' } : null;
}

export type FollowUpDialogProps = {
  open: boolean;
  onClose: () => void;
  fields: FollowUpFields;
  setField: <K extends keyof FollowUpFields>(key: K, value: FollowUpFields[K] | null) => void;
  onSubmit: () => void;
  hasChanges: boolean;
  isPending: boolean;
};

export function FollowUpDialog({
  open,
  onClose,
  fields,
  setField,
  onSubmit,
  hasChanges,
  isPending,
}: FollowUpDialogProps) {
  const status = fields.requestStatus;

  const contactedAtField = useMemo(() => (status ? getContactedAtFieldName(status) : null), [status]);

  // When no status is selected, show all fields; otherwise mirror FollowUpPanel visibility
  const noStatusSelected = status === undefined;
  const showContactedAt = noStatusSelected || contactedAtField !== null;
  const showCalledDate = noStatusSelected || contactedAtField === 'thirdContactedAt';
  const showNextFollowUpAt = noStatusSelected || !isCompleteStatus(status!);
  const showCompletionFields = noStatusSelected || (!!status && isCompleteStatus(status));

  const handleStatusChange = useCallback(
    (value: RequestStatus | null) => {
      setField('requestStatus', value);
      if (!value) return;

      // Clear completion fields when switching to non-complete status
      if (!isCompleteStatus(value)) {
        setField('outcome', null);
        setField('utilityPresence', null);
        setField('resolutionDate', null);
      }

      const autoPopulateMap: Partial<Record<RequestStatus, keyof FollowUpFields>> = {
        [RequestStatus.InProgress0]: 'firstContactedAt',
        [RequestStatus.InProgress1]: 'secondContactedAt',
        [RequestStatus.InProgress2]: 'thirdContactedAt',
        [RequestStatus.InProgress3]: 'fourthContactedAt',
        [RequestStatus.Complete]: 'resolutionDate',
      };
      const autoField = autoPopulateMap[value];

      // Clear all contacted-at and calledDate fields first, then re-allow only the relevant one
      const allContactFields: (keyof FollowUpFields)[] = [
        'firstContactedAt',
        'secondContactedAt',
        'thirdContactedAt',
        'fourthContactedAt',
        'calledDate',
      ];
      const activeContactField = getContactedAtFieldName(value);
      allContactFields.forEach((f) => {
        if (f === 'calledDate') {
          if (activeContactField !== 'thirdContactedAt') setField(f, null);
        } else if (f !== activeContactField) {
          setField(f, null);
        }
      });

      if (autoField && !fields[autoField]) {
        setField(autoField, DateTime.now().toISO() as any);
      }

      // Clear nextFollowUpAt when switching to a complete status (mirrors GeneralForm)
      if (isCompleteStatus(value)) {
        setField('nextFollowUpAt', null);
      }
    },
    [setField, fields],
  );

  const DateField = ({ fieldKey, label }: { fieldKey: keyof FollowUpFields; label: string }) => (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <DatePicker
        value={isoToDatePickerValue(fields[fieldKey] as string | undefined)}
        onChange={(v) => setField(fieldKey, (v && 'date' in v ? v.date : null) as any)}
        placeholder="No change"
      />
    </div>
  );

  // Which contacted-at field(s) to show when no status is selected
  const contactedAtFieldsWhenUnset: (keyof FollowUpFields)[] = [
    'firstContactedAt',
    'secondContactedAt',
    'thirdContactedAt',
    'fourthContactedAt',
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="civilsearch-bulk-follow-up-dialog"
        title="Bulk Update Status"
        preventDismiss={hasChanges}
        className="w-120"
      >
        <DialogBody className="flex flex-col gap-4 w-full">
          <p className="text-xs text-grey-60">
            Only fields you set here will be updated. Leave a field blank to leave it unchanged.
          </p>
          <Select
            label="Request Status"
            items={requestStatusItems}
            value={fields.requestStatus ?? ''}
            onChange={(v) => handleStatusChange(v as RequestStatus | null)}
            isClearable
            callToAction="No change"
          />
          {showContactedAt &&
            (noStatusSelected || !contactedAtField ? (
              // No status selected: show all contacted-at date fields
              contactedAtFieldsWhenUnset.map((f) => <DateField key={f} fieldKey={f} label={CONTACTED_AT_LABELS[f]!} />)
            ) : (
              <DateField fieldKey={contactedAtField!} label={CONTACTED_AT_LABELS[contactedAtField!]!} />
            ))}
          {showCalledDate && <DateField fieldKey="calledDate" label="Called Date" />}
          {showNextFollowUpAt && <DateField fieldKey="nextFollowUpAt" label="Next Follow-up At" />}
          {showCompletionFields && (
            <>
              <Select
                label="Outcome"
                items={outcomeItems}
                value={fields.outcome ?? ''}
                onChange={(v) => setField('outcome', v as Outcome | null)}
                isClearable
                callToAction="No change"
              />
              <Select
                label="Utility Presence"
                items={utilityPresenceItems}
                value={fields.utilityPresence ?? ''}
                onChange={(v) => setField('utilityPresence', v as UtilityPresence | null)}
                isClearable
                callToAction="No change"
              />
              <DateField fieldKey="resolutionDate" label="Resolution Date" />
            </>
          )}
          <div className="space-y-1">
            <Label>Append Internal Note</Label>
            <textarea
              value={fields.notesInternal ?? ''}
              onChange={(e) => setField('notesInternal', e.target.value || null)}
              rows={3}
              placeholder="Text to append to notes..."
              className="w-full rounded-md border border-grey-30 bg-transparent px-3 py-2 text-sm resize-none focus:outline-none focus:border-focus min-h-20"
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isPending || !hasChanges}>
            {isPending ? 'Updating…' : 'Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
